import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import SpinnerButton from "@/components/SpinnerButton";
import { adminApi } from "@/lib/api";

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const r = await adminApi.team();
    setMembers(r.data.members);
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Team</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, margin: 0 }}>Members</h1>
        <button className="scw-btn" onClick={() => setAdding(true)} data-testid="team-add-btn">Add member</button>
      </div>

      <div className="scw-card" style={{ padding: 0 }}>
        {members.length === 0 ? (
          <p style={{ padding: 24, color: "var(--scw-muted)" }}>No team members yet.</p>
        ) : (
          <div className="scw-table-wrap">
            <table className="scw-table scw-leads-table">
              <thead><tr><th>Name</th><th>WhatsApp</th><th>Email</th><th>Store</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.ID} data-testid={`team-row-${m.ID}`}>
                    <td data-label="Name">{m.Name}</td>
                    <td data-label="WhatsApp">{m.WhatsApp}</td>
                    <td data-label="Email">{m.Email}</td>
                    <td data-label="Store">
                      <span className="scw-pill" data-testid={`team-store-${m.ID}`}>
                        {(m.Store || "KL").toUpperCase()}
                      </span>
                    </td>
                    <td data-label="Active">{String(m.Active).toUpperCase() === "TRUE" ? "Yes" : "No"}</td>
                    <td data-label="Actions" style={{ textAlign: "right" }}>
                      <div className="scw-action-cluster">
                        <button className="scw-btn scw-btn-ghost" style={{ padding: "6px 12px" }} onClick={() => setEditing(m)} data-testid={`team-edit-${m.ID}`}>Edit</button>
                        <button className="scw-btn scw-btn-ghost" style={{ padding: "6px 12px" }}
                                onClick={async () => { await adminApi.teamUpdate(m.ID, { active: !(String(m.Active).toUpperCase() === "TRUE") }); load(); }}
                                data-testid={`team-toggle-${m.ID}`}>{String(m.Active).toUpperCase() === "TRUE" ? "Deactivate" : "Activate"}</button>
                        <button className="scw-btn scw-btn-ghost" style={{ padding: "6px 12px" }}
                                onClick={async () => { if (window.confirm("Remove this team member?")) { await adminApi.teamDelete(m.ID); load(); } }}
                                data-testid={`team-delete-${m.ID}`}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <MemberModal title="Add member" onClose={() => setAdding(false)}
                              onSave={async (b) => { await adminApi.teamCreate(b); setAdding(false); load(); }} />}
      {editing && <MemberModal title="Edit member" member={editing} onClose={() => setEditing(null)}
                                onSave={async (b) => { await adminApi.teamUpdate(editing.ID, b); setEditing(null); load(); }} />}
    </AdminLayout>
  );
}

function MemberModal({ title, member, onClose, onSave }) {
  const [name, setName] = useState(member?.Name || "");
  const [whatsapp, setWhatsapp] = useState(member?.WhatsApp || "");
  const [email, setEmail] = useState(member?.Email || "");
  const [active, setActive] = useState(member ? String(member.Active).toUpperCase() === "TRUE" : true);
  // Pass 43 — Store dropdown. Empty / legacy rows default to KL so the
  // historical workflow doesn't change. JB members get tagged here and
  // are automatically excluded from the KL duty rotation server-side.
  const [storeVal, setStoreVal] = useState((member?.Store || "KL").toUpperCase() === "JB" ? "JB" : "KL");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr(""); setLoading(true);
    try { await onSave({ name, whatsapp, email, active, store: storeVal }); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <div style={{ background: "#FFFFFF", padding: 28, width: "min(440px, 100%)" }} onClick={(e) => e.stopPropagation()} data-testid="team-modal">
        <h3 style={{ fontSize: 22, margin: "0 0 18px 0" }}>{title}</h3>
        <div style={{ marginBottom: 14 }}>
          <label className="scw-label">Name</label>
          <input className="scw-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="team-modal-name" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="scw-label">WhatsApp</label>
          <input className="scw-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} data-testid="team-modal-whatsapp" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="scw-label">Email</label>
          <input className="scw-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-modal-email" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label className="scw-label">Store</label>
          <select
            className="scw-select"
            value={storeVal}
            onChange={(e) => setStoreVal(e.target.value)}
            data-testid="team-modal-store"
          >
            <option value="KL">Kuala Lumpur</option>
            <option value="JB">Johor Bahru</option>
          </select>
          <p style={{ marginTop: 6, fontSize: 12, color: "var(--scw-muted)", lineHeight: 1.5 }}>
            JB members are excluded from the KL duty roster and receive
            all three contact channels for the JB store.
          </p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="team-modal-active" /> Active
          </label>
        </div>
        {err && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="scw-btn scw-btn-ghost" onClick={onClose}>Cancel</button>
          <SpinnerButton loading={loading} type="button" onClick={save} dataTestId="team-modal-save">Save</SpinnerButton>
        </div>
      </div>
    </div>
  );
}
